import React, { forwardRef, useEffect } from 'react'
import Currency, { currencyCb, moneyString } from '../../../../components/common/Currency'
import { CalculatorDataAsNumbers } from '../types/RoiCalculatorTypes'
import { Design } from '../../../../api/Designs/Types'
import { Chart } from 'frappe-charts/dist/frappe-charts.min.esm'
import TalkToExpertButton from '../../../../components/common/TalkToExpertButton'
import I18n from '../../../../helpers/I18n'
import { numberWithCommas } from '../../../../helpers/StringHelper'
import { CalculatorResults } from '../helpers/Calculator'

interface Props {
  calculatorDataAsNumbers: Required<CalculatorDataAsNumbers>
  calculatorResults: CalculatorResults | []
  exchangeRate: ExchangeRate
  design?: Design
  localizedPath: string
}

const Summary = forwardRef<HTMLDivElement, Props & React.HTMLProps<HTMLDivElement>>(
  ({ calculatorDataAsNumbers, calculatorResults, exchangeRate, design, localizedPath }, ref) => {
    const processesIncluded =
      calculatorDataAsNumbers.automatedProcess > 0 && calculatorDataAsNumbers.manualProcess > 0

    const [totalPaybackTime, unitsToBreakEven, fiveYearSavings, cumulativeValues] =
      calculatorResults

    const roiResultsToClipboard = (): string => {
      let url = `${localizedPath}?`

      if (calculatorDataAsNumbers) {
        const calculatorDataToParams = Object.keys(calculatorDataAsNumbers)
          .map(key => `${key}=${calculatorDataAsNumbers[key]}`)
          .join('&')

        url += `${calculatorDataToParams}`
      }

      if (design) {
        url += `&designNumber=${design.design_number}`
      }

      return `${url}&autoSubmit=true`
    }

    useEffect(() => {
      if (cumulativeValues) {
        const graphData = {
          labels: ['1', '2', '3', '4', '5'],
          datasets: [
            {
              values: calculatorDataAsNumbers.equipmentHardwareCost
                ? cumulativeValues.savings
                : null,
            },
          ],
        }

        new Chart('#graph', {
          data: graphData,
          type: 'bar',
          height: 350,
          colors: ['#1ac876'],
          axisOptions: {
            shortenYAxisNumbers: true,
          },
          tooltipOptions: {
            formatTooltipX: d =>
              `${I18n.t('views.roi_calculator.savings', {
                year_count: d,
              })} ${
                processesIncluded
                  ? `<div class='frappe-units'>${numberWithCommas(
                      cumulativeValues.units[d - 1]
                    )} Units</div>`
                  : ''
              } `,
            formatTooltipY: d =>
              `${moneyString(d, design?.currency_code || exchangeRate.currency_code, 0)} ${
                design?.currency_code || exchangeRate.currency_code
              }`,
          },
        })
      }
    }, [calculatorDataAsNumbers, cumulativeValues, design, exchangeRate])

    return (
      <div ref={ref} className='roi-calculator-summary'>
        <div className='roi-calculator-summary-container'>
          <h2 className='roi-calculator-summary-heading'>
            {I18n.t('views.roi_calculator.summary_heading')}
          </h2>
          <div className='roi-calculator-summary-panel'>
            {design && (
              <div className='roi-calculator-summary-design'>
                <img
                  className='roi-calculator-summary-design-image'
                  src={design.image_thumb_path}
                  alt={design.name}
                />
                <div className='roi-calculator-summary-design-stats'>
                  <p className='roi-calculator-summary-design-name'>{design.name}</p>
                  <p className='roi-calculator-summary-design-price'>
                    <Currency
                      currencyCb={currencyCb(design.total_price)}
                      symbol={design.currency_symbol}
                      code={design.currency_code}
                    />
                  </p>
                  <a
                    className='roi-calculator-summary-design-number'
                    href={`/designs/${design.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {design.design_number}
                  </a>
                </div>
              </div>
            )}
            <div className='roi-calculator-summary-results'>
              <div className='roi-calculator-summary-stat'>
                <p className='roi-calculator-summarty-stat-number capitalize'>
                  {totalPaybackTime ? totalPaybackTime : '-'}
                </p>
                <h3 className='roi-calculator-summary-stat-title'>
                  {I18n.t('views.roi_calculator.payback_time')}
                </h3>
              </div>
              <div className='roi-calculator-summary-stat'>
                <p className='roi-calculator-summarty-stat-number capitalize'>
                  {unitsToBreakEven && unitsToBreakEven > 0
                    ? numberWithCommas(unitsToBreakEven)
                    : '-'}
                </p>
                <h3 className='roi-calculator-summary-stat-title'>
                  {I18n.t('views.roi_calculator.units_break_even')}
                </h3>
              </div>
              <div className='roi-calculator-summary-stat'>
                <p className='roi-calculator-summarty-stat-number'>
                  {fiveYearSavings && fiveYearSavings > 0
                    ? moneyString(
                        fiveYearSavings,
                        design?.currency_code || exchangeRate.currency_code,
                        0
                      )
                    : '-'}
                </p>
                <h3 className='roi-calculator-summary-stat-title'>
                  {I18n.t('views.roi_calculator.savings', { year_count: 5 })}
                </h3>
              </div>
            </div>
            <div className='roi-calculator-summary-graph-container'>
              <p className='roi-calculator-summary-graph-text-left'>
                {I18n.t('views.roi_calculator.graph_savings')}
              </p>
              <div id='graph' className='roi-calculator-summary-graph'></div>
              <p className='roi-calculator-summary-graph-text-bottom'>
                {I18n.t('views.roi_calculator.graph_years')}
              </p>
            </div>
            <div className='roi-calculator-summary-actions'>
              {/* PDF download button to be enabled once PDF is created */}
              {/* <button
                className='roi-calculator-summary-download-pdf'
                onClick={() => window.print()}
              >
                <i className='far fa-arrow-to-bottom' />
                {I18n.t('views.roi_calculator.download_pdf')}
              </button> */}
              <button
                className='roi-calculator-summary-copy-link'
                data-controller='copy-to-clipboard-button'
                data-copy-to-clipboard-button-payload-value={roiResultsToClipboard()}
                data-copy-to-clipboard-button-success-text-value='Copied!'
              >
                <i className='fal fa-link' />
                {I18n.t('views.roi_calculator.copy_link')}
              </button>
            </div>
          </div>
          <TalkToExpertButton
            openNewWindow={true}
            trackingKey='roi_calculator_talk_to_expert'
            className='roi-calculator-talk-to-expert'
            text={I18n.t('views.common.talk_to_expert')}
          />
        </div>
      </div>
    )
  }
)

Summary.displayName = 'Summary'
export default Summary
